// Two-step verification (spec 2026-10-01-03): the API calls behind the login
// code step, the forced-enrollment and step-up pages and the Security tab.
// The backend decides everything (internal/handlers/auth/mfa_*.go); this file
// only carries requests and the shapes the pages share.

import { apiClient } from '../api/client'
import type {
    LoginResponse,
    MfaChallenge as MFAChallenge,
    MfaFactor as MFAFactor,
    MfaStatus as MFAStatus,
    MfaEnrollment as MFAEnrollment,
    MfaConfirmResult as MFAConfirmResult,
    MfaCodeSent as MFACodeSent,
} from '../api/schema'
import { apiErrorCode, parseApiError } from './apiError'
// The i18next singleton, not '../i18n/i18n': importing the setup module from a
// plain lib would initialize i18n (and react-i18next) in every test that
// imports this file indirectly, breaking tests that mock react-i18next.
import i18n from 'i18next'

export type { LoginResponse, MFAChallenge, MFAFactor, MFAStatus, MFAEnrollment, MFAConfirmResult, MFACodeSent }

/** The language code messages (e-mail, SMS) are written in. */
function messageLang(): string {
    return (i18n.language || 'en').slice(0, 2)
}

/** A challenge waiting for a code: from a login, a refresh or a step-up. */
export interface PendingChallenge {
    token: string
    factors: MFAFactor[]
}

export function challengeFromLogin(resp: { mfa_challenge_token?: string; mfa_factors?: MFAFactor[] }): PendingChallenge {
    return {
        token: resp.mfa_challenge_token ?? '',
        factors: resp.mfa_factors ?? [],
    }
}

/** A kind of factor stored on the user: one per kind, so the kind names it. */
export type FactorKind = 'totp' | 'email' | 'sms'

/**
 * A failed API call, keeping the backend's problem code (`mfa_invalid_code`,
 * `mfa_locked`, ...) so the pages can translate it.
 */
export class MfaApiError extends Error {
    code: string | null
    /** Seconds to wait, from Retry-After (rate limits). */
    retryAfter: number | null
    constructor(error: unknown, response?: Response) {
        super(parseApiError(error)?.title ?? 'request failed')
        this.code = apiErrorCode(error)
        const retry = Number(response?.headers.get('Retry-After'))
        this.retryAfter = Number.isFinite(retry) && retry > 0 ? retry : null
    }
}

export interface VerifyInput {
    /** For an e-mail or SMS code, the token sendMfaCode returned with it. */
    challengeToken: string
    /** The factor's kind, or `passkey` for the passkey entry. */
    factor: string
    code?: string
    /** The passkey entry: the sealed session and the browser's assertion. */
    passkeySession?: string
    credential?: Record<string, unknown>
}

export async function verifyMfa(org: string, input: VerifyInput): Promise<LoginResponse> {
    const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/auth/mfa/verify', {
        params: { path: { orgCode: org } },
        body: {
            challenge_token: input.challengeToken,
            factor: input.factor as 'totp' | 'email' | 'sms' | 'passkey',
            code: input.code,
            passkey_session: input.passkeySession,
            credential: input.credential,
        },
    })
    if (error || !data) throw new MfaApiError(error)
    return data
}

/**
 * Sends a code for an e-mail or SMS factor of a challenge. The answer's
 * `challenge_token` carries the code: verify with it.
 */
export async function sendMfaCode(org: string, challengeToken: string, factor: 'email' | 'sms'): Promise<MFACodeSent> {
    const { data, error, response } = await apiClient.POST('/api/v1/organizations/{orgCode}/auth/mfa/send', {
        params: { path: { orgCode: org } },
        body: { challenge_token: challengeToken, factor, lang: messageLang() },
    })
    if (error || !data) throw new MfaApiError(error, response)
    return data.data
}

/** Starts the WebAuthn assertion answering a challenge's passkey entry. */
export async function beginMfaPasskey(org: string, challengeToken: string): Promise<{ options: Record<string, any>; session: string }> {
    const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/auth/mfa/passkey/begin', {
        params: { path: { orgCode: org } },
        body: { challenge_token: challengeToken },
    })
    if (error || !data) throw new MfaApiError(error)
    return data as unknown as { options: Record<string, any>; session: string }
}

/** Describes a challenge received as a bare token (SSO callback, password reset). */
export async function describeChallenge(org: string, token: string): Promise<PendingChallenge> {
    const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/auth/mfa/challenge', {
        params: { path: { orgCode: org } },
        body: { challenge_token: token },
    })
    if (error || !data) throw new MfaApiError(error)
    return challengeFromLogin(data)
}

/** Opens a step-up challenge for the current session (bearer). */
export async function stepUpChallenge(org: string): Promise<PendingChallenge> {
    const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/auth/mfa/challenge', {
        params: { path: { orgCode: org } },
    })
    if (error || !data) throw new MfaApiError(error)
    return challengeFromLogin(data)
}

export async function fetchMfaStatus(org: string): Promise<MFAStatus> {
    const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/users/me/mfa', {
        params: { path: { orgCode: org } },
    })
    if (error || !data) throw new MfaApiError(error)
    return data.data
}

export async function beginTotpEnrollment(org: string): Promise<MFAEnrollment> {
    return beginEnrollment(org, { kind: 'totp' })
}

/**
 * Starts enrolling a factor; an e-mail or SMS factor gets its first code at
 * once. Nothing is stored until confirmEnrollment: keep `enrollment_token`.
 */
export async function beginEnrollment(org: string, input: { kind: FactorKind; phone?: string }): Promise<MFAEnrollment> {
    const { data, error, response } = await apiClient.POST('/api/v1/organizations/{orgCode}/users/me/mfa/factors', {
        params: { path: { orgCode: org } },
        body: { ...input, lang: messageLang() },
    })
    if (error || !data) throw new MfaApiError(error, response)
    return data.data
}

/**
 * Sends a new code for an e-mail or SMS enrollment. The answer's
 * `enrollment_token` carries the new code: confirm with it.
 */
export async function resendEnrollmentCode(org: string, kind: 'email' | 'sms', enrollmentToken: string): Promise<MFACodeSent> {
    const { data, error, response } = await apiClient.POST('/api/v1/organizations/{orgCode}/users/me/mfa/factors/{kind}/send', {
        params: { path: { orgCode: org, kind } },
        body: { enrollment_token: enrollmentToken, lang: messageLang() },
    })
    if (error || !data) throw new MfaApiError(error, response)
    return data.data
}

export async function confirmEnrollment(org: string, kind: FactorKind, enrollmentToken: string, code: string): Promise<MFAConfirmResult> {
    const { data, error } = await apiClient.POST('/api/v1/organizations/{orgCode}/users/me/mfa/factors/{kind}/confirm', {
        params: { path: { orgCode: org, kind } },
        body: { enrollment_token: enrollmentToken, code },
    })
    if (error || !data) throw new MfaApiError(error)
    return data.data
}

export async function deleteFactor(org: string, kind: FactorKind): Promise<void> {
    const { error } = await apiClient.DELETE('/api/v1/organizations/{orgCode}/users/me/mfa/factors/{kind}', {
        params: { path: { orgCode: org, kind } },
    })
    if (error) throw new MfaApiError(error)
}

export async function resetMemberMfa(org: string, userUid: string): Promise<number> {
    const { data, error } = await apiClient.DELETE('/api/v1/organizations/{orgCode}/members/{userUid}/mfa', {
        params: { path: { orgCode: org, userUid } },
    })
    if (error || !data) throw new MfaApiError(error)
    return data.data.factors_removed
}

/** sessionStorage key of a challenge a refresh handed out, for /mfa-verify. */
export const PENDING_CHALLENGE_KEY = 'mfaPendingChallenge'

/**
 * The i18n key (namespace `auth`) of an MFA error code, falling back to a
 * generic message.
 */
export function mfaErrorKey(code: string | null | undefined): string {
    switch (code) {
        case 'mfa_invalid_code':
            return 'mfa.errors.invalid_code'
        case 'mfa_locked':
            return 'mfa.errors.locked'
        case 'mfa_challenge_expired':
        case 'mfa_challenge_invalid':
            return 'mfa.errors.expired'
        case 'mfa_factor_not_allowed':
            return 'mfa.errors.not_allowed'
        case 'mfa_last_factor':
            return 'mfa.errors.last_factor'
        case 'mfa_verification_required':
            return 'mfa.errors.verification_required'
        case 'mfa_passkey_required':
            return 'mfa.errors.passkey_required'
        case 'mfa_no_usable_factor':
            return 'mfa.errors.no_usable_factor'
        case 'mfa_send_rate_limited':
            return 'mfa.errors.send_rate_limited'
        case 'mfa_sms_cap_reached':
            return 'mfa.errors.sms_cap_reached'
        case 'mfa_delivery_failed':
            return 'mfa.errors.delivery_failed'
        case 'mfa_phone_invalid':
            return 'mfa.errors.phone_invalid'
        case 'mfa_email_missing':
            return 'mfa.errors.email_missing'
        case 'mfa_duplicate_factor':
            return 'mfa.errors.duplicate_factor'
        case 'mfa_passkey_failed':
            return 'mfa.errors.passkey_failed'
        case 'mfa_enrollment_invalid':
            return 'mfa.errors.enrollment_invalid'
        default:
            return 'mfa.errors.generic'
    }
}
